import { useCallback, useRef, useState } from 'react';

import * as Sentry from '@sentry/react';

import { vnextApiClient } from '@/services/vnext-api';
import {
  readDisplayCurrency,
  writeDisplayCurrency,
  type ConsumptionSource,
  type DisplayCurrency,
  type EconomicsQuery,
  type EconomicsRefreshResult,
  type EconomicsSummary,
  type ProjectEconomicsDetail,
  type ProjectEconomicsPage,
  type ProjectEconomicsQuery,
  type UnattributedCosts,
} from '@/types/economics';
import { toError } from '@/utils/errors';
import { UNCHANGED, usePoll, withEtag } from './usePoll';

/** The figures move when the ledgers refresh, every half hour; the poll is a
 * cheap revalidation in between. */
export const ECONOMICS_POLL_MS = 120_000;

/** The viewer's display currency, remembered in this browser. */
export function useDisplayCurrency(): [DisplayCurrency, (currency: DisplayCurrency) => void] {
  const [currency, setCurrency] = useState<DisplayCurrency>(readDisplayCurrency);
  const choose = useCallback((next: DisplayCurrency) => {
    writeDisplayCurrency(next);
    setCurrency(next);
  }, []);
  return [currency, choose];
}

/**
 * Polls one conditional economics read. The query is a `refetchDeps` entry: a
 * different filter or display currency is a different token space, so it
 * reads again in full rather than revalidating against the old token.
 */
function useConditionalEconomics<Q, T>(
  query: Q,
  read: (
    query: Q,
    options: { signal: AbortSignal; etag: string | null }
  ) => Promise<'not-modified' | { data: T; etag: string | null }>
) {
  const key = JSON.stringify(query);
  const queryRef = useRef(query);
  queryRef.current = query;

  const poll = usePoll<T>({
    interval: ECONOMICS_POLL_MS,
    refetchDeps: [key],
    queryFn: async ({ signal, etag, manual }) => {
      const result = await read(queryRef.current, { signal, etag: manual ? null : etag });
      return result === 'not-modified' ? UNCHANGED : withEtag(result.data, result.etag);
    },
    onError: (err) => Sentry.captureException(toError(err)),
  });

  return {
    data: poll.data ?? null,
    isLoading: poll.isLoading,
    isFetching: poll.isFetching,
    error: poll.error === undefined ? null : toError(poll.error),
    refetch: poll.refetch,
  };
}

export function useEconomicsSummary(query: EconomicsQuery) {
  const { data, ...rest } = useConditionalEconomics<EconomicsQuery, EconomicsSummary>(
    query,
    (q, options) => vnextApiClient.getEconomicsSummary(q, options)
  );
  return { summary: data, ...rest };
}

export function useProjectEconomics(query: ProjectEconomicsQuery) {
  const { data, ...rest } = useConditionalEconomics<ProjectEconomicsQuery, ProjectEconomicsPage>(
    query,
    (q, options) => vnextApiClient.listProjectEconomics(q, options)
  );
  return { page: data, ...rest };
}

/** One piece of work with every cost line behind it; read again when the
 * work or the display currency changes. */
export function useProjectEconomicsDetail(
  item: { source: ConsumptionSource; sourceRef: string } | null,
  displayCurrency: DisplayCurrency
) {
  const poll = usePoll<ProjectEconomicsDetail>({
    interval: null,
    enabled: item !== null,
    refetchDeps: [item?.source, item?.sourceRef, displayCurrency],
    queryFn: ({ signal }) =>
      vnextApiClient.getProjectEconomics(item!.source, item!.sourceRef, displayCurrency, {
        signal,
      }),
    onError: (err) => Sentry.captureException(toError(err)),
  });
  return {
    detail: poll.data ?? null,
    isLoading: poll.isLoading,
    error: poll.error === undefined ? null : toError(poll.error),
  };
}

export function useUnattributedCosts(query: Omit<EconomicsQuery, 'teamId'>) {
  const key = JSON.stringify(query);
  const queryRef = useRef(query);
  queryRef.current = query;
  const poll = usePoll<UnattributedCosts>({
    interval: null,
    refetchDeps: [key],
    queryFn: ({ signal }) => vnextApiClient.getUnattributedCosts(queryRef.current, { signal }),
    onError: (err) => Sentry.captureException(toError(err)),
  });
  return {
    unattributed: poll.data ?? null,
    isLoading: poll.isLoading,
    error: poll.error === undefined ? null : toError(poll.error),
    refetch: poll.refetch,
  };
}

/**
 * Refresh the ledgers now, then let the caller re-read what it shows.
 * `onRefreshed` runs after a refresh that completed, including one that could
 * not read Backoffice — its own costs were still refreshed.
 */
export function useEconomicsRefresh(onRefreshed?: () => Promise<void> | void) {
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [result, setResult] = useState<EconomicsRefreshResult | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const onRefreshedRef = useRef(onRefreshed);
  onRefreshedRef.current = onRefreshed;

  const refresh = useCallback(async () => {
    setIsRefreshing(true);
    setError(null);
    try {
      const outcome = await vnextApiClient.refreshEconomics();
      setResult(outcome);
      await onRefreshedRef.current?.();
    } catch (err) {
      const e = toError(err);
      Sentry.captureException(e);
      setError(e);
    } finally {
      setIsRefreshing(false);
    }
  }, []);

  return { refresh, isRefreshing, result, error };
}
